<template>
  <div class="container">
    <div class="row justify-content-center align-items-center min-vh-100">
      <div class="col-md-6 col-lg-5">
        <div class="login-box bg-dark p-4 shadow rounded-3">
          <!-- Logo/Título -->
          <div class="text-center mb-4">
            <img src="@/assets/images/zapiola-logo.png" alt="semillero del oeste logo" class="img-fluid col-5 pb-4">
          </div>

          <form @submit.prevent="handleSubmit" class="needs-validation" novalidate>
            <!-- Campo Usuario -->
            <div class="form-floating mb-3">
              <input
                type="text"
                class="form-control"
                :class="{ 'is-invalid': v$.username.$error }"
                id="username"
                v-model="formData.username"
                placeholder="Usuario"
                required
              >
              <label for="username">
                <i class="fas fa-user text-muted me-2"></i>Usuario
              </label>
              <div class="invalid-feedback" v-if="v$.username.$error">
                {{ v$.username.$errors[0].$message }}
              </div>
            </div>

            <!-- Campo Contraseña -->
            <div class="form-floating mb-3">
              <input
                :type="showPassword ? 'text' : 'password'"
                class="form-control"
                :class="{ 'is-invalid': v$.password.$error }"
                id="password"
                v-model="formData.password"
                placeholder="Contraseña"
                required
              >
              <label for="password">
                <i class="fas fa-lock text-muted me-2"></i>Contraseña
              </label>
              <span 
                class="password-toggle"
                @click="showPassword = !showPassword"
              >
                <i :class="showPassword ? 'fas fa-eye-slash' : 'fas fa-eye'"></i>
              </span>
              <div class="invalid-feedback" v-if="v$.password.$error">
                {{ v$.password.$errors[0].$message }}
              </div>
            </div>

            <!-- Mensaje de error -->
            <div v-if="errorMessage" class="alert alert-danger mb-3">
              {{ errorMessage }}
            </div>

            <!-- dont have an account -->
            <p class="text-muted mb-3">
              ¿No tienes cuenta? 
              <router-link to="/crear-cuenta" class="text-primary">Regístrate</router-link>
            </p>

            <!-- Botón Submit -->
            <button
              type="submit"
              class="btn btn-primary w-100 mb-3 py-3"
              :disabled="isLoading"
            >
              <span v-if="isLoading" class="spinner-border spinner-border-sm me-2"></span>
              {{ isLoading ? 'Iniciando sesión...' : 'Iniciar Sesión' }}
            </button>
          </form>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, onMounted } from 'vue'
import { useVuelidate } from '@vuelidate/core'
import { required, minLength } from '@vuelidate/validators'
import { useRouter, useRoute } from 'vue-router'
import { login } from '@/services/auth'
import { useToast } from 'vue-toastification'

const router = useRouter()
const route = useRoute()
const toast = useToast()

// Estado del formulario
const formData = reactive({
  username: '',
  password: ''
})

// Estado UI
const showPassword = ref(false)
const isLoading = ref(false)
const errorMessage = ref('')

// Reglas de validación
const rules = {
  username: { required, minLength: minLength(4) },
  password: { required, minLength: minLength(6) }
}

const v$ = useVuelidate(rules, formData)

// Manejo del submit
const handleSubmit = async () => {
  try {
    errorMessage.value = '' // Limpia errores previos
    const isFormValid = await v$.value.$validate()
    
    if (!isFormValid) return
    
    isLoading.value = true
    
    // Usa la función login del servicio de autenticación
    const response = await login(formData.username, formData.password)
    
    // Redirigir al usuario según sus permisos y la ruta intentada
    const redirectPath = route.query.redirect || 
                        (response.is_admin ? '/admin/productos' : '/mi-cuenta')
    
    router.push(redirectPath)
    
  } catch (error) {
    console.error('Error en login:', error)
    errorMessage.value = error.message
    toast.error('Error al iniciar sesión')
  } finally {
    isLoading.value = false
  }
}

// Verificar si hay una ruta guardada al montar el componente
onMounted(() => {
  const intendedRoute = localStorage.getItem('intendedRoute')
  if (intendedRoute) {
    localStorage.removeItem('intendedRoute')
  }

  // seteo el titulo de la pagina
  document.title = 'Iniciar Sesión - Zapiola Growshop'
})
</script>

<style scoped>
.login-box {
  transition: all 0.3s ease;
}

.form-floating input {
  padding-right: 2.5rem;
}

.password-toggle {
  position: absolute;
  top: 50%;
  right: 1rem;
  transform: translateY(-50%);
  cursor: pointer;
  color: #6c757d;
  z-index: 10;
}

.password-toggle:hover {
  color: #495057;
}

.form-control:focus {
  box-shadow: 0 0 0 0.25rem rgba(13, 110, 253, 0.15);
}

.btn-primary {
  transition: all 0.2s ease;
}

.btn-primary:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 4px 8px rgba(13, 110, 253, 0.2);
}

.btn-primary:active:not(:disabled) {
  transform: translateY(0);
}

@media (max-width: 576px) {
  .login-box {
    margin: 1rem;
  }
}
</style>